import { createGtm } from '@gtm-support/vue-gtm'
import { createHead } from '@unhead/vue/client'
import 'sima-suite-components-vue3/dist/style.css'
import { createPinia } from 'pinia'
import 'sima-suite-components-vue3/dist/icons.css'
import { configureSimaSuiteComponents } from 'sima-suite-components-vue3'
import { configureTranslations } from 'sima-suite-translator-vue3'
import { createApp } from 'vue'

import themes from '@/assets/themes'

import './assets/tailwind.css'
import './assets/fonts/fonts.css'
import './assets/volkswagen-integrator.css'
import './veeValidateRules.ts'

import { useHost } from '@/composables'
import { setVolkswagenIntegratorBoundary } from '@/integratorBoundary'
import DependenciesInjector from '@/plugins/DependencyInjector'
import { initMonitoring, Release } from '@/plugins/sentry.ts'
import loadDidomi from '@/utils/didomiWrapper'

import packageInfo from '../package.json'
import App from './App.vue'
import { configureVeeValidateLocales, loadConfiguration } from './configuration'
import { getRouter } from './router'
import translations from './translations'

configureTranslations(translations)
configureVeeValidateLocales()
const { initializeHost } = useHost()
const configuration = await loadConfiguration()

const appRoot = document.querySelector<HTMLElement>('#app')
setVolkswagenIntegratorBoundary(appRoot, configuration.useVolkswagenIntegrator)

if (configuration.didomiId) {
  loadDidomi(document, configuration.didomiId)
}

const theme = themes[configuration.host] || themes.default

initializeHost({
  brandColor: theme.brandColor,
  brandFont: theme.brandFont,
  brandLogo: theme.brandLogo,
  brandsWeb: configuration.brandsWeb,
  conditionsUrl: '',
  cookiesUrl: '',
  didomiId: configuration.didomiId,
  id: configuration.host,
  legalAdviserUrl: configuration.legalAdviserUrl,
  mapsKey: configuration.mapsKey,
  privacyPolicyUrl: configuration.privacyPolicyUrl,
  useFooter: configuration.useVolkswagenIntegrator ? false : configuration.useFooter,
  useLocalCookieBanner: configuration.useLocalCookieBanner,
  useNewRGPDPolicy: configuration.useNewRGPDPolicy,
  useVolkswagenIntegrator: configuration.useVolkswagenIntegrator ?? false,
  webOwnerName: configuration.webOwnerName
})

const head = createHead()

const app = createApp(App)
app.use(createPinia())
const router = getRouter()
app.use(router)
app.use(DependenciesInjector, { backendUrl: configuration.backendUrl })
app.use(head)

const themeProduct = {
  color: {
    primary: {
      '500': theme.brandColor
    }
  }
}

if (import.meta.env.PROD) {
  const { name, version } = packageInfo
  initMonitoring(app, {
    backendUrl: configuration.backendUrl,
    dsn: 'https://d2a7114c526d480d844b3559d53cd1be@o1427692.ingest.sentry.io/4505431958290432',
    release: new Release(name, version),
    router
  })
}

configureSimaSuiteComponents(app, themeProduct)

/* Replace with you GTag id */
app.use(
  createGtm({
    id: configuration.googleAnalyticsUA,
    parentElement: document.head
  })
)
app.mount('#app')
